/* Custom Design V2 journey. No dependencies and no scroll-jacking: native scrolling throughout.
   - IntersectionObserver picks the active chapter (the one crossing the middle of the screen) and
     reveals chapters/final panel once.
   - One passive, rAF-throttled scroll handler reads three rects and writes CSS variables only
     (--hp hero, --p journey, --d active chapter); all motion is CSS.
   Without this script the page is a complete, static editorial page. */
(() => {
  const root = document.querySelector('[data-cjd]');
  if (!root || !('IntersectionObserver' in window)) return;

  const $$ = (s, r = root) => Array.from(r.querySelectorAll(s));
  const reduce = matchMedia('(prefers-reduced-motion: reduce)');
  const hero = root.querySelector('[data-cjd-hero]');
  const journey = root.querySelector('[data-cjd-journey]');
  const stage = root.querySelector('[data-cjd-stage]');
  const chapters = $$('[data-cjd-ch]');
  const layers = stage ? $$('[data-l]', stage) : [];
  const shots = stage ? $$('[data-shot]', stage) : [];
  const figs = stage ? $$('[data-fig]', stage) : [];
  const links = $$('[data-cjd-go]');
  const nowN = root.querySelector('[data-cjd-now-n]');
  const nowT = root.querySelector('[data-cjd-now-t]');
  const pad = (n) => String(n).padStart(2, '0');
  const clamp = (v) => (v < 0 ? 0 : v > 1 ? 1 : v);
  let active = 0;
  let wasTimer = 0;

  function activate(n) {
    if (n === active || !chapters[n - 1]) return;
    const prev = active;
    active = n;
    root.dataset.step = n;
    root.dataset.atmo = chapters[n - 1].dataset.atmo;
    root.style.setProperty('--a', n);
    layers.forEach((l) => {
      const k = +l.dataset.l;
      l.dataset.state = k < n ? 'past' : k === n ? 'now' : 'next';
    });
    shots.forEach((s) => {
      const k = +s.dataset.shot;
      s.classList.toggle('is-on', k === n);
      s.classList.toggle('was-on', k === prev);
    });
    clearTimeout(wasTimer);
    wasTimer = setTimeout(() => shots.forEach((s) => s.classList.remove('was-on')), 1600);
    figs.forEach((f) => f.classList.toggle('is-on', +f.dataset.fig === n));
    links.forEach((a) => (+a.dataset.cjdGo === n ? a.setAttribute('aria-current', 'step') : a.removeAttribute('aria-current')));
    if (nowN && links[n - 1]) {
      nowN.textContent = pad(n);
      nowT.textContent = links[n - 1].querySelector('.cjd-rail__t').textContent;
    }
    frame();
  }

  // Scroll-linked variables. Reads first, then writes; nothing else touches layout.
  let ticking = false;
  const last = {};
  const put = (el, k, v) => {
    const s = v.toFixed(3);
    if (last[k] !== s) { last[k] = s; el.style.setProperty(k, s); }
  };
  function frame() {
    ticking = false;
    const vh = window.innerHeight;
    const h = hero ? hero.getBoundingClientRect() : null;
    const j = journey.getBoundingClientRect();
    const c = active ? chapters[active - 1].getBoundingClientRect() : null;
    if (h) put(root, '--hp', reduce.matches ? 0 : clamp(-h.top / h.height));
    put(root, '--p', clamp((vh * 0.5 - j.top) / j.height));
    if (stage && c) put(stage, '--d', reduce.matches ? 1 : clamp((vh * 0.5 - c.top) / (c.height * 0.55)));
  }
  const onScroll = () => {
    if (!ticking) { ticking = true; requestAnimationFrame(frame); }
  };

  // Active chapter: whichever crosses the vertical middle of the viewport.
  const picker = new IntersectionObserver((entries) => {
    entries.forEach((e) => { if (e.isIntersecting) activate(+e.target.dataset.cjdCh); });
  }, { rootMargin: '-50% 0px -50% 0px' });
  chapters.forEach((c) => picker.observe(c));

  // One-time reveals.
  const revealer = new IntersectionObserver((entries) => {
    entries.forEach((e) => {
      if (!e.isIntersecting) return;
      e.target.classList.add('is-in');
      revealer.unobserve(e.target);
    });
  }, { rootMargin: '0px 0px -18% 0px', threshold: 0.12 });
  chapters.concat($$('[data-cjd-final]')).forEach((el) => revealer.observe(el));

  // Stage photos sit fully clipped until their chapter, which lazy-loading reads as "never visible".
  // On desktop, load them once the journey is near; on mobile the stage is hidden and never loads.
  if (stage && matchMedia('(min-width: 990px)').matches) {
    const warm = new IntersectionObserver((e) => {
      if (!e[0].isIntersecting) return;
      $$('img', stage).forEach((img) => { img.loading = 'eager'; });
      warm.disconnect();
    }, { rootMargin: '150% 0px' });
    warm.observe(journey);
  }

  // The hero fills the screen below the site header.
  const header = document.querySelector('.section-header, header');
  if (header) root.style.setProperty('--cjd-head', Math.round(header.getBoundingClientRect().bottom + scrollY) + 'px');

  // Pause the hero's ambient loops when it is off screen.
  if (hero) new IntersectionObserver((e) => hero.classList.toggle('is-off', !e[0].isIntersecting)).observe(hero);

  // "See how it works" and the rail glide to a chapter, then move focus there for keyboard users.
  function go(target, e) {
    if (!target) return;
    e.preventDefault();
    target.scrollIntoView({ behavior: reduce.matches ? 'auto' : 'smooth', block: 'start' });
    const ch = target.matches('[data-cjd-ch]') ? target : chapters[0];
    const heading = ch && ch.querySelector('h3');
    if (heading) { heading.setAttribute('tabindex', '-1'); heading.focus({ preventScroll: true }); }
  }
  root.addEventListener('click', (e) => {
    const how = e.target.closest('[data-cjd-how]');
    if (how) return go(journey, e);
    const link = e.target.closest('[data-cjd-go]');
    if (link) return go(chapters[+link.dataset.cjdGo - 1], e);

    // Sketch swatches tint the illustration only.
    const sw = e.target.closest('[data-cjd-gem], [data-cjd-metal]');
    if (!sw) return;
    const gem = sw.hasAttribute('data-cjd-gem');
    const [a, b] = sw.getAttribute(gem ? 'data-cjd-gem' : 'data-cjd-metal').split(',');
    root.style.setProperty(gem ? '--cjd-gem' : '--cjd-metal-a', a);
    root.style.setProperty(gem ? '--cjd-gem-deep' : '--cjd-metal-b', b);
    $$('.cjd-sw', sw.parentElement).forEach((x) => x.setAttribute('aria-pressed', String(x === sw)));
  });

  window.addEventListener('scroll', onScroll, { passive: true });
  window.addEventListener('resize', onScroll, { passive: true });
  reduce.addEventListener('change', frame);
  root.classList.add('is-live');
  activate(1);
})();
